// @flow
import React, { useState } from 'react';
import { get } from 'lodash';
import DayPicker, { DateUtils } from 'react-day-picker';
import { type FieldProps } from 'formik';
import moment from 'moment';

import * as MomentLocaleUtils from 'Components/frameworks/datepicker/Datepicker.utils';

import { DatePickerCaption } from './DatePickerCaption.jsx';
import { themes } from './DatePicker.themes';

export type DatePickerProps = {
  ...FieldProps,
  isDateRange: boolean,
  locale?: string,
  maxDate?: Date,
  minDate?: Date,
  theme?: string,
  disabledDays?: {
    after: Date,
    before: Date,
  },
};

type DateValue = { from: Date, to: Date };

const { isDate, isDayAfter, isDayBefore, isSameDay } = DateUtils;
const DATEPICKER_HORIZON_IN_MONTHS = 24;

export const DatePicker = (props: DatePickerProps) => {
  const {
    field,
    form,
    isDateRange,
    locale = 'en',
    maxDate = moment()
      .add({ months: DATEPICKER_HORIZON_IN_MONTHS })
      .endOf('month')
      .toDate(),
    minDate = moment()
      .startOf('month')
      .toDate(),
    theme = 'default',
    disabledDays = [],
  } = props;

  const { from, to }: DateValue = get(form.values, field.name, {});

  const [month, setMonth] = useState(minDate);
  const [hovered, setHovered] = useState(isDateRange ? from : null);

  const setFromDay = day => {
    form.setFieldValue(field.name, { from: day, to: null });
    setHovered(null);
  };

  const setToDay = day => {
    form.setFieldValue(field.name, { from, to: day });
    setHovered(null);
  };

  const resetDays = () => {
    form.setFieldValue(field.name, { from: null, to: null });
    setHovered(null);
  };

  const isSelectingStartDay = !isDate(from);
  const isOverridingStartDay = day => {
    const startAndEndDaysAreSelected = isDate(from) && isDate(to);
    const dayIsBeforeStartDay = isDate(from) && isDayBefore(day, from);
    return startAndEndDaysAreSelected || dayIsBeforeStartDay;
  };
  const isSelectingSameDay = day => isDate(from) && isSameDay(day, from);
  const isSelectingEndDay = day =>
    isDate(from) && !isSelectingSameDay(day) && !isOverridingStartDay(day);

  const handleDayClick = day => {
    if (!isDateRange) {
      setFromDay(day);
    } else {
      if (isSelectingStartDay || isOverridingStartDay(day)) {
        setFromDay(day);
      }
      if (isSelectingSameDay(day)) {
        resetDays();
      }
      if (isSelectingEndDay(day)) {
        setToDay(day);
      }
    }
  };

  const handleDayMouseEnter = day => {
    if (isDateRange && isSelectingEndDay(day)) {
      setHovered(day);
    }
  };

  const changeMonth = (date: Date, number: number) => {
    if (!isDate(date)) return;
    const targetMonth = moment(date)
      .add({ months: number })
      .toDate();
    if (isDayBefore(targetMonth, minDate)) setMonth(minDate);
    if (isDayAfter(targetMonth, maxDate)) setMonth(maxDate);
    setMonth(targetMonth);
  };

  const {
    thDay,
    thDayEnd,
    thDayHovered,
    thDayPicker,
    thDaysHovered,
    thDaysSelected,
    thDayStart,
  } = themes[theme];
  const selectedDays = { from, to };
  const modifiers = {
    [thDay]: !isDateRange && from,
    [thDayStart]: isDateRange && from,
    [thDayEnd]: to,
    [thDayHovered]: hovered && !to ? hovered : undefined,
    [thDaysHovered]: from && hovered ? { from, to: hovered } : [],
    [thDaysSelected]: from && to ? selectedDays : [],
    selected: selectedDays,
  };

  return (
    <DayPicker
      canChangeMonth
      firstDayOfWeek={moment.localeData().firstDayOfWeek()}
      fixedWeeks
      locale={locale}
      localeUtils={MomentLocaleUtils}
      month={month}
      showOutsideDays
      showWeekDays
      captionElement={
        <DatePickerCaption
          fromMonth={minDate}
          locale={locale}
          month={month}
          onNext={() => changeMonth(month, 1)}
          onPrevious={() => changeMonth(month, -1)}
          toMonth={maxDate}
        />
      }
      classNames={thDayPicker}
      disabledDays={[{ before: minDate, after: maxDate }, ...disabledDays]}
      modifiers={modifiers}
      onDayClick={handleDayClick}
      onDayMouseEnter={handleDayMouseEnter}
      onDayTouchEnd={handleDayMouseEnter}
    />
  );
};
